import React, {useContext, useEffect} from 'react';
import {FlatList, View, Platform} from 'react-native';

import Header from '../../components/elements/Header';
import Text from '../../components/elements/Text';
import CartItemCard from '../../components/modules/CartItemCard';
import Button from '../../components/elements/Button';
import CartPriceOverview from '../../components/modules/CartPriceOverview';
import ProductLimitPopup from '../../components/modules/ProductLimitPopup';
import Popup from '../../components/elements/Popup';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES} from '../../utils/Constants';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import {shopContext} from '../../contexts/ShopContext';
import {SafeAreaView} from 'react-native-safe-area-context';
import {convertDateToTime, DLog} from '../../utils/HelperMethods';
import {HTTP_METHODS, QUANTITY_CHECK} from '../../utils/URLConstants';
import {fetchApi} from '../../utils/ApiHelper';
import {CloseIcon, EmptyFeed, LeftArrowIcon} from '../../utils/SvgIcons';

const MyCart = ({navigation, setCartBeingViewed, setProductBeingViewed}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);

  const [showQuantityPopup, setShowQuantityPopup] = React.useState(false);
  const [showLimitReachedPopup, setShowLimitReachedPopup] =
    React.useState(false);
  const [limitReachedItems, setLimitReachedItems] = React.useState([]);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);
  const ShopC = useContext(shopContext);

  const onAdjustCart = (data, tag) => {
    let cartItems = [...ShopC.cartProducts];

    if (tag == 'insufficient_quantiy') {
      setShowQuantityPopup(true);
      ShopC.cartProducts.forEach((p, idx) => {
        data.forEach(i => {
          if (p.id == i.id) {
            cartItems[idx] = {...p, productQty: i.availableQuantity};
          }
        });
      });
    }
    if (tag == 'limit_exceeded') {
      // Reorder-limit trim: build the list of trimmed products (with when each
      // is next available) so the popup can explain WHY items left the cart,
      // then keep the existing trim-to-available logic.
      let productsUnavailable = [];
      ShopC.cartProducts.forEach((p, idx) => {
        data.forEach(i => {
          if (p.id == i.id) {
            productsUnavailable.push({
              title: p.title,
              dateAvailable: convertDateToTime(i.nextResetDate),
            });
            cartItems[idx] = {...p, productQty: i.availableQuantityToOrder};
          }
        });
      });
      cartItems = cartItems.filter(item => item.productQty > 0);
      setLimitReachedItems(productsUnavailable);
      setShowLimitReachedPopup(true);
    }
    ShopC.setCartProducts(cartItems);
    DLog('cartItems', cartItems);
  };

  //check in the cart if any product's ordered quantity is greater than available quantity
  const quantityCheck = () => {
    // DLog('MyCart quantityCheck', 'cartProducts', ShopC.cartProducts);
    if (ShopC.cartProducts.length > 0) {
      let params = ShopC.cartProducts
        .filter(p => p.price == 0)
        .map(item => {
          return {id: parseInt(item.id), orderedQuantity: item.productQty};
        });
      // DLog('MyCart quantityCheck', 'params', params);
      fetchApi(HTTP_METHODS.POST, QUANTITY_CHECK, params)
        .then(res => {
          // DLog('RES => ', res.data);
        })
        .catch(err => {
          // DLog('FAILED RES => ', err.data.data);
          if (typeof err != 'string') {
            let data = err.data.data;
            if (data.insufficient_quantiy.length > 0) {
              onAdjustCart(data.insufficient_quantiy, 'insufficient_quantiy');
            } else if (data.limit_exceeded.length > 0) {
              onAdjustCart(data.limit_exceeded, 'limit_exceeded');
            }
          }
          // esc.showError(err);
        });
    }
  };

  useEffect(() => {
    quantityCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- quantity check intentionally keyed to cart contents only
  }, [ShopC.cartProducts]);

  const renderQuantityConfirmation = () => {
    return (
      <Popup
        title={'Your cart has been adjusted!'}
        visible={showQuantityPopup}
        textAlign={'center'}
        description={`You’ve exceeded the max amount of free items in your cart.\n
We’ve updated the complimentary items in your cart to reflect the amount of items you can order.\n
You are free to move forward and checkout when you’re ready!`}>
        <Button
          style={[styles.button(0)]}
          title={'Return To Cart'}
          buttonType={BUTTON_TYPES.Filled}
          onPress={() => {
            setShowQuantityPopup(false);
          }}
          titleColor={colors.textOnAccent}
        />
      </Popup>
    );
  };

  const renderLimitReachedConfirmation = () => {
    return (
      <ProductLimitPopup
        visible={showLimitReachedPopup}
        title="Free Product Limit Reached"
        description={
          'You have reached the limit for complimentary products offered by Diathrive Health.\n\nGood news! You can return to place your order when supplies are available to you.'
        }
        limitReachedItems={limitReachedItems}>
        <Button
          style={[styles.button(0)]}
          title={'Return To Cart'}
          buttonType={BUTTON_TYPES.Filled}
          onPress={() => {
            setShowLimitReachedPopup(false);
          }}
          titleColor={colors.textOnAccent}
        />
      </ProductLimitPopup>
    );
  };

  const renderEmptyListView = () => {
    return (
      <View style={styles.emptyListContainer}>
        <EmptyFeed
          width={Platform.OS === 'web' ? 120 : 161}
          height={Platform.OS === 'web' ? 140 : 193}
        />
      </View>
    );
  };

  const renderHeaderTitle = () => (
    <Text
      variation="Subtitle1Bold"
      color={colors.link}
      style={styles.marginHorizontal24}
      topMargin={32}>
      Selected Products{' '}
      <Text variation="Subtitle1Regular" color={colors.link}>
        ({ShopC.cartProducts.length})
      </Text>
    </Text>
  );

  const renderFooter = () => {
    if (ShopC.cartProducts.length > 0) {
      return (
        <CartPriceOverview
          sectionTitle={'Price Overview'}
          instruction={'*No shipping address selected.'}
        />
      );
    } else {
      return null;
    }
  };

  const renderCartItem = ({item, index}) => {
    // DLog('renderCartItem', item);
    return (
      <CartItemCard
        item={item}
        index={index}
        setProductBeingViewed={setProductBeingViewed}
      />
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={[styles.marginHorizontal24, styles.marginTop16]}>
        <Header
          title="My Cart"
          onPressLeftIcon={
            Platform.OS === 'web'
              ? () => setCartBeingViewed(false)
              : navigation.goBack
          }
          leftImage={Platform.OS === 'web' ? <CloseIcon /> : <LeftArrowIcon />}
        />
      </View>
      <FlatList
        data={ShopC.cartProducts}
        renderItem={renderCartItem}
        keyExtractor={(item, index) => index}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={renderEmptyListView}
        ListHeaderComponent={renderHeaderTitle}
        ListFooterComponent={renderFooter}
      />
      <View style={styles.buttonsContainer(24)}>
        <Button
          style={styles.button(0)}
          buttonType={BUTTON_TYPES.Bordered}
          title={'Keep Shopping'}
          onPress={() =>
            // Back to the catalog in one tap. 'Shopping' is a tab inside
            // HomeTab, so go via the tab host (pops this stack too); on web
            // the cart is a side panel — just close it.
            Platform.OS === 'web'
              ? setCartBeingViewed(false)
              : navigation.navigate('HomeTab', {screen: 'Shopping'})
          }
        />
        <Button
          disabled={ShopC.cartProducts.length == 0 || !ShopC.canUserCheckout}
          style={styles.button(16)}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          title={'Checkout'}
          onPress={() =>
            Platform.OS === 'web'
              ? navigation('/checkout')
              : navigation.navigate('Checkout')
          }
        />
      </View>
      {renderQuantityConfirmation()}
      {renderLimitReachedConfirmation()}
    </SafeAreaView>
  );
};

export default MyCart;
